import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select } from '../../../../types/redux/pages/PagesStore';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
  toggleTitleBarLoading,
} from '../../../../redux/pages/actions';
import {
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../../types/redux/pages/pageTypes';
import useTitleBarSelect from '../../../../hooks/useTitleBarSelect';
import {
  activeSectionSelector,
  createActiveFundSelectorBySection,
} from '../../../../redux/pages/selectors';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import NoDataMessage from '../../../feedback/NoDataMessage.component';
import {
  ComplianceType,
  getRstFixedLrstKeyRows,
  rstFixedStressTestsAreEmpty,
  type RstFixedPagePayload,
} from './services/api.routes';
import { useRstFixedDropdownOptions, useRstFixedPage } from './services/queries';
import FixedRstTable from './components/fixedRstTable.component';
import {
  buildFilRiskColumns,
  SifRiskSpreadFixedColumns,
  act40FixedColumns,
  buildAct40FixedData,
  buildProspectusFixedData,
  buildRiaifFixedData,
  buildSifRiskColumns,
  buildTimeSeriesFixedData,
  buildUcitsFixedData,
  prospectusFixedColumns,
  riaifFixedColumns,
  timeSeriesFixedColumns,
  ucitsFixedColumns,
} from './components/fixedRst.data';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';

type PrimaryTableConfig = {
  title: string;
  complianceType?: ComplianceType;
  buildData:
  | ((data: any[], complianceChecklist: any, redemptionLevel: number | null) => any[])
  | ((data: any[], redemptionLevel: number | null) => any[]);
  columns: CustomColumn<any>[];
};

type RstFixedConfig = {
  primaryTable: PrimaryTableConfig | null;
};

// Section-level RST fixed config.
// Backend config should eventually own this, removing need for data mapping logic here.
// Keep `aifmd-rst-fixed` aligned if this is refactored, it was too messy for now as it had aifmd-specific data fetching logic.
function getRstFixedConfig(sectionID?: string): RstFixedConfig | null {
  if (!sectionID) return null;

  if (sectionID === 'ucits') {
    return {
      primaryTable: {
        title: 'UCITS Restrictions Under Redemptions',
        complianceType: 'UCITS',
        buildData: buildUcitsFixedData,
        columns: ucitsFixedColumns,
      },
    };
  } else if (sectionID === 'riaif') {
    return {
      primaryTable: {
        title: 'RIAIF Restrictions Under Redemptions',
        complianceType: 'RIAIF',
        buildData: buildRiaifFixedData,
        columns: riaifFixedColumns,
      },
    };
  } else if (sectionID === 'act40') {
    return {
      primaryTable: {
        title: '40 Act Restrictions Under Redemptions',
        complianceType: 'ACT40',
        buildData: buildAct40FixedData,
        columns: act40FixedColumns,
      },
    };
  } else if (sectionID === 'fil') {
    return {
      primaryTable: {
        title: 'AIFM Leverage Under Redemptions',
        buildData: buildFilRiskColumns,
        columns: SifRiskSpreadFixedColumns,
      },
    };
  } else if (sectionID === 'sif-raif') {
    return {
      primaryTable: {
        title: 'CSSF Risk Spreading Under Redemptions',
        buildData: buildSifRiskColumns,
        columns: SifRiskSpreadFixedColumns,
      },
    };
  } else if (sectionID === 'nurs') {
    return {
      primaryTable: null, // According to peter, this probably should have one in the future, but no current implementation
    };
  } else if (sectionID === 'qiaif') {
    return {
      primaryTable: null,
    };
  } else if (sectionID === 'partii') {
    return {
      primaryTable: null,
    };
  } else if (sectionID === 'fivg') {
    return {
      primaryTable: null,
    };
  } else if (sectionID === 'trust') {
    return {
      primaryTable: null,
    };
  }

  return null;
}

function getRedemptionLevel(keyValues: any[], selectedOption: string | null) {
  if (!selectedOption || !keyValues) return null;
  for (let i = 0; i < keyValues.length; i++) {
    if (keyValues[i].value === selectedOption) {
      const splitString = keyValues[i].label.split('%');
      try {
        return parseInt(splitString[0]);
      } catch {
        return null;
      }
    }
  }
  return null;
}

const RSTFixedPage: FC<FundInfoComponentProps> = (props) => {
  const activeSection = useSelector(activeSectionSelector);
  useSelector(createActiveFundSelectorBySection(activeSection));
  const dispatch = useDispatch();
  const sectionID = activeSection || props.section;
  const config = getRstFixedConfig(sectionID);
  const [scenariosOptions, setScenariosOptions] = useState<
    { label: string; value: string }[] | null
  >(null);
  /** True after LRST keys arrived and the scenario select was dispatched to the title bar. */
  const [scenarioTitleBarReady, setScenarioTitleBarReady] = useState(false);
  const selectedScenario = useTitleBarSelect('scenarioSelection');

  const dropdownOptionsQuery = useRstFixedDropdownOptions(props.fundId);

  const dropdownLrstResult = useMemo(
    () => getRstFixedLrstKeyRows(dropdownOptionsQuery.data),
    [dropdownOptionsQuery.data],
  );

  const firstLrstKey = dropdownLrstResult.rows[0]?.[1] ?? null;

  const selectedLrstKey = useMemo(() => {
    if (!scenarioTitleBarReady) return null;
    if (
      selectedScenario &&
      dropdownLrstResult.rows.some(([, value]) => value === selectedScenario)
    ) {
      return selectedScenario;
    }
    return firstLrstKey;
  }, [
    dropdownLrstResult.rows,
    firstLrstKey,
    scenarioTitleBarReady,
    selectedScenario,
  ]);

  const pageLrstKey = selectedLrstKey;

  const pageQuery = useRstFixedPage(
    props.fundId,
    props.positionDate,
    pageLrstKey,
  );


  // Drop stale title-bar scenario selection when fund or date changes (Redux may still hold the old key).
  useEffect(() => {
    setScenarioTitleBarReady(false);
    dispatch(
      removeTitleBarComponent(
        'scenarioSelection',
        REMOVE_SELECT_FROM_TITLEBAR,
      ),
    );
  }, [dispatch, props.fundId, props.positionDate]);

  useEffect(() => {
    dispatch(toggleTitleBarLoading(true));
    setScenarioTitleBarReady(false);

    if (!dropdownOptionsQuery.data) {
      setScenariosOptions(null);
      dispatch(toggleTitleBarLoading(false));
      return () => {
        dispatch(
          removeTitleBarComponent(
            'scenarioSelection',
            REMOVE_SELECT_FROM_TITLEBAR,
          ),
        );
      };
    }

    const { rows } = getRstFixedLrstKeyRows(dropdownOptionsQuery.data);
    const options =
      rows.length > 0
        ? rows.map(([label, value]) => ({ label, value }))
        : null;
    setScenariosOptions(options);

    if (options?.length) {
      const selectsToAdd: Select[] = [
        {
          titleBarTitle: 'Select Scenario',
          titleBarKey: 'scenarioSelection',
          values: options,
          displayOnOverviewPage: false,
          aValueIsAlwaysSelected: true,
          currentValue: options[0].value,
        },
      ];
      dispatch(
        addAdditionalTitleBarComponents(
          selectsToAdd,
          APPEND_NEW_SELECT_TO_TITLEBAR,
        ),
      );
      setScenarioTitleBarReady(true);
    }
    dispatch(toggleTitleBarLoading(false));
    return () => {
      dispatch(
        removeTitleBarComponent(
          'scenarioSelection',
          REMOVE_SELECT_FROM_TITLEBAR,
        ),
      );
    };
  }, [dispatch, dropdownOptionsQuery.data, props.fundId, props.positionDate]);


  const redemptionLevel = useMemo(
    () => getRedemptionLevel(scenariosOptions || [], selectedLrstKey),
    [scenariosOptions, selectedLrstKey],
  );

  if (!config) {
    return (
      <NoDataMessage message={`No configuration found for the section ${sectionID}`} />
    );
  }

  const dropdownNoDataMessage =
    !dropdownOptionsQuery.isLoading &&
    (dropdownLrstResult.backendError ||
      (!dropdownLrstResult.rows.length
        ? 'No stress test data is available for this fund and position date.'
        : null));

  // Calculate card width based on number of tables: 3 tables = 4 (1/3 width), 2 tables = 6 (1/2 width)
  const numberOfTables = config.primaryTable ? 3 : 2;
  const cardWidth = numberOfTables === 3 ? 4 : 6;

  return (
    <QueryBoundary
      isLoading={
        dropdownOptionsQuery.isLoading
        || (!dropdownNoDataMessage && !scenarioTitleBarReady)
        || pageQuery.isLoading
      }
      isFetching={
        dropdownOptionsQuery.isFetching || pageQuery.isFetching
      }
      isError={!!dropdownOptionsQuery.error || !!pageQuery.error}
      error={dropdownOptionsQuery.error || pageQuery.error}
      data={
        dropdownNoDataMessage
          ? null
          : pageQuery.data?.data
      }
      noDataComponent={
        dropdownNoDataMessage ? (
          <NoDataMessage message={dropdownNoDataMessage} />
        ) : undefined
      }
      noDataMessage="No Data Available."
      errorMessage="There was an error retrieving the data. The RiskSystem administrator has been notified."
    >
      {(payload: RstFixedPagePayload) => {
        const positionDateResolved =
          pageQuery.data?.selected_position_date || '';
        const { rows: lrstRows } = dropdownLrstResult;
        const stressTestsEmpty = rstFixedStressTestsAreEmpty(
          payload?.stress_test_data,
        );
        const firstScenarioKey = lrstRows[0]?.[1] ?? null;
        const scenarioKey = selectedLrstKey ?? firstScenarioKey;
        const stressForTables = payload?.stress_test_data as any;
        const complianceChecklist = payload?.compliance_checklist;

        const emptyStressBanner =
          stressTestsEmpty &&
          'No stress test data is available for this fund and position date.';

        const bannerMessage = emptyStressBanner || null;

        if (bannerMessage) {
          return <NoDataMessage message={bannerMessage} />;
        }

        return (
          <>
            {config.primaryTable && (
              <FixedRstTable<any>
                scenario={scenarioKey || undefined}
                positionDate={positionDateResolved}
                columns={config.primaryTable.columns}
                title={config.primaryTable.title}
                data={
                  config.primaryTable.complianceType
                    ? (config.primaryTable.buildData as (
                      data: any[],
                      complianceChecklist: any,
                      redemptionLevel: number | null,
                    ) => any[])(
                      stressForTables,
                      complianceChecklist,
                      redemptionLevel,
                    )
                    : (config.primaryTable.buildData as (
                      data: any[],
                      redemptionLevel: number | null,
                    ) => any[])(stressForTables, redemptionLevel)
                }
                fixed={cardWidth}
              />
            )}
            <FixedRstTable
              scenario={scenarioKey || undefined}
              positionDate={positionDateResolved}
              columns={prospectusFixedColumns}
              title="Prospectus Restrictions Under Redemptions"
              data={buildProspectusFixedData(stressForTables, redemptionLevel)}
              fixed={cardWidth}
            />
            <FixedRstTable
              scenario={scenarioKey || undefined}
              positionDate={positionDateResolved}
              columns={timeSeriesFixedColumns}
              title="TimeSeries Data under Redemptions"
              data={buildTimeSeriesFixedData(stressForTables, redemptionLevel)}
              fixed={cardWidth}
            />
          </>
        );
      }}
    </QueryBoundary>
  );
};

export default RSTFixedPage;
